/**
 * DPDP consent + KYC verification service.
 *
 * Thin axios wrapper. Catalogue is public (no auth header needed —
 * users hit it during signup before they have a token); everything
 * else is bearer-gated by the gateway.
 */
import api from './api';

const consentService = {
  // ─── Consent ─────────────────────────────────────────────────────

  /** Canonical consent catalogue — wording, version, mandatory flags. */
  getCatalogue: () => api.get('/consent/catalogue'),

  /** List the caller's recorded consents. */
  listMine: () => api.get('/consent/me'),

  /** Record one consent decision.
   *
   * @param {{ consent_type: string, consent_given: boolean, consent_text: string, policy_version?: string }} body
   */
  record: (body) => api.post('/consent', body),

  /** Preview the impact of revoking a given consent type. */
  revokeImpact: (consentType) =>
    api.get(`/consent/${encodeURIComponent(consentType)}/revoke-impact`),

  /** Revoke a granted consent. */
  revoke: (consentType) =>
    api.delete(`/consent/${encodeURIComponent(consentType)}`),

  // ─── KYC verification (PAN / Aadhaar / GSTIN / IFSC) ─────────────

  verifyPan: (body) => api.post('/kyc/pan/verify', body),
  sendAadhaarOtp: (body) => api.post('/kyc/aadhaar/otp/send', body),
  verifyAadhaarOtp: (body) => api.post('/kyc/aadhaar/otp/verify', body),
  searchGstin: (body) => api.post('/kyc/gstin/search', body),
  ifscLookup: (ifsc) => api.get(`/kyc/bank/${encodeURIComponent(ifsc)}`),
  listMyKyc: () => api.get('/kyc/me'),

  // ─── Compliance documents (KYC uploads) ──────────────────────────
  /**
   * Upload a KYC / compliance document.
   *
   * @param {{ file: File, category: string, subtype: string, name?: string,
   *          documentNumber?: string, issuedAt?: string, expiresAt?: string,
   *          notes?: string }} args
   *
   * Standard categories used by the onboarding flow:
   *  - identity      (subtype: pan_card | aadhaar_front | aadhaar_back | passport)
   *  - address_proof (subtype: utility_bill | rental_agreement | bank_statement)
   *  - vehicle       (subtype: driving_license | rc_book | insurance | puc)
   *  - facility      (subtype: facility_photo | site_plan | fire_noc)
   *  - financial     (subtype: bank_statement | cancelled_cheque)
   */
  uploadDocument: ({ file, category, subtype, name, documentNumber, issuedAt, expiresAt, notes }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category);
    fd.append('subtype', subtype);
    if (name) fd.append('name', name);
    if (documentNumber) fd.append('document_number', documentNumber);
    if (issuedAt) fd.append('issued_at', issuedAt);
    if (expiresAt) fd.append('expires_at', expiresAt);
    if (notes) fd.append('notes', notes);
    return api.post('/compliance/documents/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** List the caller's compliance docs.
   *
   *  Hits ``/compliance/documents/hub`` — the backend's hub view that
   *  returns ``{items, counts}``. The bare ``/compliance/documents``
   *  prefix doesn't have a list handler, so the old call was 404-ing
   *  every consumer (KycPendingBanner, RequireKyc, KycOnboardingWizard).
   *  Callers already read ``data.items``, so /hub's response shape
   *  is a drop-in.
   */
  listMyDocuments: (params = {}) => api.get('/compliance/documents/hub', { params }),

  // ─── Bank account (host / logistics-partner payout setup) ────────
  /**
   * Save a bank account for payouts. The IFSC is validated server-side
   * via the same Sandbox lookup the IFSC tab uses, so we don't need to
   * duplicate the call here. Account number is masked at rest — we
   * keep only the last 4 digits in the response.
   *
   * Backend route lives in marketplace-service under `/host-bank-accounts`
   * (gateway prefix in routes.py). Field names there are
   * ``beneficiary_name`` / ``ifsc`` / ``is_default`` — we translate from
   * the wizard's friendlier names so the wizard form doesn't need to know
   * the route's quirks.
   */
  addBankAccount: ({
    account_holder_name,
    account_number,
    ifsc_code,
    is_primary,
    nickname,
  }) =>
    api.post('/host-bank-accounts', {
      beneficiary_name: account_holder_name,
      account_number,
      ifsc: ifsc_code,
      is_default: !!is_primary,
      nickname,
    }),
  listBankAccounts: () => api.get('/host-bank-accounts'),
};

export default consentService;
